import { useState } from "react";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { Text } from "@/components/ui/Text";
import { Badge } from "@/components/ui/badge";
import { PrivateImage } from "./PrivateImage";
import type { ProductGalleryProps } from "@/types/creative-library";

export function ProductGallery({
  items,
  selected,
  fixed,
  usedCount,
  onChange,
  onPreview,
  boardPinned = [],
}: ProductGalleryProps) {
  const { t } = useTranslation("app");
  const [query, setQuery] = useState("");
  const visible = items.filter((item) =>
    item.title.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()),
  );
  return (
    <div className="flex flex-col gap-3">
      <Text size="sm" color="muted">
        {t("creativeLibrary.productGalleryHelp")}
      </Text>
      {items.length === 0 && (
        <Text size="sm" color="muted">
          {t("creativeLibrary.productGalleryEmpty")}
        </Text>
      )}
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t("creativeLibrary.searchReferences")}
        aria-label={t("creativeLibrary.searchReferences")}
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {visible.map((item) => {
          const pinned = fixed.includes(item.token);
          const checked = pinned || selected.includes(item.token);
          return (
            <article
              key={item.token}
              className="flex cursor-pointer flex-col overflow-hidden rounded-xl border border-border bg-background"
            >
              <button
                type="button"
                aria-label={item.title}
                onClick={() => onPreview(item)}
              >
                <PrivateImage
                  source={item.mediaUrl}
                  alt={item.title}
                  className="aspect-square w-full object-contain p-3 rounded-none"
                />
              </button>
              <div className="flex flex-col gap-2 border-t border-border p-3">
                <label className="flex items-start gap-2">
                  <Checkbox
                    checked={checked}
                    disabled={pinned || (!checked && usedCount >= 4)}
                    data-testid={`product-reference-${item.token}`}
                    onCheckedChange={(value) =>
                      onChange(
                        value === true
                          ? [...selected, item.token]
                          : selected.filter((token) => token !== item.token),
                      )
                    }
                  />
                  <Text size="xs" className="min-w-0 break-words">
                    {item.title}
                  </Text>
                </label>
                <Badge variant="outline" className="self-start">
                  {t(`creativeLibrary.roles.${item.role}`)}
                </Badge>
                {pinned && (
                  <Text size="xs" color="muted">
                    {t(
                      boardPinned.includes(item.token)
                        ? "creativeLibrary.includedByBoard"
                        : "creativeLibrary.includedByBrand",
                    )}
                  </Text>
                )}
                <Text size="xs" color="muted">
                  {t(`creativeLibrary.usage.${item.usage}`)}
                </Text>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
